import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert, Button, Modal, Space, Spin, Tooltip, Typography,
} from 'antd';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { PageTabsContext, PageTabBarExtraContext } from 'util/PageTabs';
import { useForecastRun, usePlanQuery, useRefreshPlan } from './hooks';
import * as fmt from './format';
import brewFromRecommendation from './brewFromRecommendation';
import PlanTab from './PlanTab';
import ScheduleTab from './ScheduleTab';
import RulesTab from './RulesTab';
import AccuracyTab from './AccuracyTab';
import EventsTab from './EventsTab';
import BeerDrawer from './BeerDrawer';
import BrewModal from './BrewModal';
import EventModal from './EventModal';
import './Forecasting.less';

const { Text } = Typography;

export { brewFromRecommendation };

const DataStatus = ({ plan, running }) => {
  const { run } = plan;
  return (
    <div className="forecasting-status">
      <Space size="large" wrap>
        <Text type="secondary">
          Forecast run
          {' '}
          <Text strong>{run ? dayjs(run.runTs).format('ddd D MMM, h:mma') : '—'}</Text>
          {run && ` (sales through ${fmt.date(run.demandThrough)})`}
        </Text>
        <Text type="secondary">
          Stock snapshot
          {' '}
          <Text strong>{fmt.date(plan.snapshotDate)}</Text>
        </Text>
        {running && <Text type="secondary">Forecast is rebuilding</Text>}
      </Space>
    </div>
  );
};

DataStatus.propTypes = {
  plan: PropTypes.shape({
    run: PropTypes.shape({ runTs: PropTypes.string, demandThrough: PropTypes.string }),
    snapshotDate: PropTypes.string,
  }).isRequired,
  running: PropTypes.bool.isRequired,
};

const Forecasting = () => {
  const tab = useContext(PageTabsContext) || 'plan';
  const setTabBarExtra = useContext(PageTabBarExtraContext);
  const planQuery = usePlanQuery();
  const refresh = useRefreshPlan();
  const forecastRun = useForecastRun();
  const startForecast = forecastRun.start;
  const [detail, setDetail] = useState(null);
  const [brewModal, setBrewModal] = useState({ open: false, initial: null });
  const [eventModal, setEventModal] = useState({ open: false, initial: null });

  const plan = planQuery.data;
  const canEdit = !!plan?.canEdit;
  const beer = useMemo(
    () => (plan && detail ? plan.beers.find((b) => b.beerName === detail.beerName) : null),
    [plan, detail],
  );

  const stepBeer = useCallback((delta) => {
    setDetail((current) => {
      if (!current) return current;
      const i = current.order.indexOf(current.beerName);
      if (i < 0) return current;
      const nextName = current.order[i + delta];
      if (!nextName) return current;
      return { ...current, beerName: nextName };
    });
  }, []);

  const openBrewModal = useCallback((initial) => setBrewModal({ open: true, initial }), []);
  const openEventModal = useCallback((initial) => setEventModal({ open: true, initial }), []);
  const confirmRun = useCallback(() => {
    Modal.confirm({
      title: 'Run the forecast now?',
      content: 'Rebuilds the Core forecast in BigQuery. Takes about 5 minutes. '
        + 'The page updates when it finishes.',
      okText: 'Run forecast',
      onOk: () => startForecast(),
    });
  }, [startForecast]);

  const refreshPlan = refresh.mutate;

  useEffect(() => () => setTabBarExtra(null), [setTabBarExtra]);

  useEffect(() => {
    if (!plan || tab === 'plan') {
      setTabBarExtra(null);
      return;
    }
    setTabBarExtra(
      <Space>
        {canEdit && (
          <Tooltip title="Rebuild the forecast in BigQuery (about 5 minutes)">
            <span>
              <Button
                icon={<PlayCircleOutlined />}
                loading={forecastRun.running}
                disabled={forecastRun.statusUnknown || refresh.isLoading}
                onClick={confirmRun}
              >
                Run forecast
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="Re-read forecast and stock from BigQuery (otherwise cached for 15 minutes)">
          <span>
            <Button
              icon={<ReloadOutlined />}
              loading={refresh.isLoading}
              disabled={forecastRun.running}
              onClick={refreshPlan}
            >
              Refresh data
            </Button>
          </span>
        </Tooltip>
      </Space>,
    );
  }, [
    canEdit,
    confirmRun,
    forecastRun.running,
    forecastRun.statusUnknown,
    plan,
    refresh.isLoading,
    refreshPlan,
    setTabBarExtra,
    tab,
  ]);
  const scheduleRecommendation = useCallback(
    (rec) => openBrewModal(brewFromRecommendation(rec)),
    [openBrewModal],
  );

  if (planQuery.isLoading) {
    return (
      <div id="forecasting-page" className="forecasting-loading">
        <Spin tip="Loading forecast, stock and tanks from BigQuery…" />
      </div>
    );
  }

  if (planQuery.isError) {
    return (
      <div id="forecasting-page">
        <Alert
          type="error"
          showIcon
          message="Could not load the forecast"
          description={planQuery.error?.response?.data?.error || planQuery.error?.message}
          action={<Button onClick={() => planQuery.refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  return (
    <div id="forecasting-page" className={tab === 'plan' ? 'forecasting-page-flow' : undefined}>
      {tab !== 'plan' && <DataStatus plan={plan} running={forecastRun.running} />}

      {tab === 'plan' && (
        <PlanTab
          plan={plan}
          canEdit={canEdit}
          running={forecastRun.running}
          runDisabled={forecastRun.statusUnknown || refresh.isLoading}
          refreshLoading={refresh.isLoading}
          onRunForecast={confirmRun}
          onRefresh={refreshPlan}
          onOpenBeer={(beerName, order) => setDetail({ beerName, order })}
        />
      )}
      {tab === 'schedule' && (
        <ScheduleTab plan={plan} canEdit={canEdit} onOpenBrewModal={openBrewModal} />
      )}
      {tab === 'rules' && <RulesTab plan={plan} canEdit={canEdit} />}
      {tab === 'events' && (
        <EventsTab plan={plan} canEdit={canEdit} onOpenEvent={openEventModal} />
      )}
      {tab === 'accuracy' && <AccuracyTab />}

      <BeerDrawer
        beer={beer}
        order={detail?.order || []}
        plan={plan}
        canEdit={canEdit}
        onClose={() => setDetail(null)}
        onStep={stepBeer}
        onScheduleRecommendation={scheduleRecommendation}
        onEditBrew={openBrewModal}
        onOpenEvent={openEventModal}
      />
      <BrewModal
        open={brewModal.open}
        initial={brewModal.initial}
        plan={plan}
        onClose={() => setBrewModal({ open: false, initial: null })}
      />
      <EventModal
        open={eventModal.open}
        initial={eventModal.initial}
        plan={plan}
        onClose={() => setEventModal({ open: false, initial: null })}
      />
    </div>
  );
};

export default Forecasting;
